import React from 'react';
import PlaytomicMatchEmbed from './PlaytomicMatchEmbed';
import FormattedContent from '../FormattedContent';
import playtomicLogo from './assets/playtomic-logo.png';
import './UpdateCard.css';

/**
 * UpdateCard - Display individual player updates (question, match, or thought)
 * Type-specific rendering with unified interface
 * Now displays formatted HTML content from RichTextEditor
 */
const UpdateCard = ({ update, onDelete, isOwnUpdate = false }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge pending">⏳ Pending</span>;
      case 'answered':
        return <span className="status-badge answered">✅ Answered</span>;
      case 'resolved':
        return <span className="status-badge resolved">✔️ Resolved</span>;
      default:
        return null;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'question':
        return '❓';
      case 'match':
        return <img src={playtomicLogo} alt="" className="playtomic-icon" />;
      case 'thought':
        return '💭';
      default:
        return '📝';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'question':
        return 'Question';
      case 'match':
        return 'Match Shared';
      case 'thought':
        return 'Thought';
      default:
        return 'Update';
    }
  };

  const typeColor = {
    question: '#007bff',
    match: '#28a745',
    thought: '#9b59b6'
  };

  return (
    <div className="update-card" style={{ borderLeftColor: typeColor[update.type] }}>
      {/* Header */}
      <div className="update-header">
        <div className="update-meta">
          <span className="update-type">
            {getTypeIcon(update.type)} {getTypeLabel(update.type)}
          </span>
          <span className="update-time">
            {formatDate(update.createdAt)}
          </span>
        </div>
        <div className="update-actions">
          {getStatusBadge(update.status)}
          {isOwnUpdate && (
            <button
              className="delete-btn"
              onClick={onDelete}
              title="Delete update"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      {/* Type-Specific Content */}
      <div className="update-content">
        {update.type === 'question' && (
          <>
            <div className="question-content">
              <FormattedContent content={update.questionText} className="question-text" />
              {update.urgencyLevel && update.urgencyLevel !== 'routine' && (
                <span className={`urgency-badge ${update.urgencyLevel}`}>
                  {update.urgencyLevel === 'soon' && '🟡 Soon'}
                  {update.urgencyLevel === 'urgent' && '🔴 Urgent'}
                </span>
              )}
            </div>
            {update.skillId && (
              <p className="context-label">
                <strong>Skill:</strong> {update.skillId}
              </p>
            )}
          </>
        )}

        {update.type === 'match' && (
          <>
            <div className="match-content">
              {update.matchThoughts && (
                <div className="match-thoughts">
                  <strong>My Thoughts:</strong>
                  <FormattedContent content={update.matchThoughts} />
                </div>
              )}
              <PlaytomicMatchEmbed
                playtomicUrl={update.playtomicUrl}
              />
            </div>
          </>
        )}

        {update.type === 'thought' && (
          <div className="thought-content">
            <FormattedContent content={update.thoughtText} className="thought-text" />
          </div>
        )}
      </div>

      {/* Coach Reply */}
      {update.coachReply && (
        <div className="coach-reply">
          <div className="reply-header">
            <span className="reply-label">💬 Coach Reply</span>
            {update.answeredAt && (
              <span className="reply-time">
                {formatDate(update.answeredAt)}
              </span>
            )}
          </div>
          <FormattedContent content={update.coachReply} className="reply-text" />
        </div>
      )}

      {/* No Reply Yet */}
      {!update.coachReply && update.status === 'pending' && (
        <div className="no-reply">
          <p>Waiting for coach reply...</p>
        </div>
      )}
    </div>
  );
};

export default UpdateCard;
